<template>
  <div>
    <!-- Modal -->
    <b-modal
      id="nova-conversa-modal"
      v-model="visivel"
      title="Nova conversa"
      size="md"
      hide-footer
      @hidden="resetar"
    >
      <div style="min-width: 260px;">
        <b-form-group
          label="WhatsApp"
          label-size="sm"
        >
          <div class="d-flex">
            <b-form-select
              v-model="codigoPais"
              :options="paises"
              size="sm"
              class="mr-2"
              style="max-width: 90px"
              @change="aplicarMascara"
            />
            <input
              ref="input"
              v-model="numero"
              v-mask="mascaraAtual"
              type="tel"
              inputmode="numeric"
              :class="['form-control form-control-sm', { 'border-success': contatoEncontrado } ]"
              :disabled="checandoContato"
              placeholder="Digite o número"
              @blur="filtrarNumeros(); verificarContatoSeNecessario()"
            >
          </div>
        </b-form-group>

        <!-- Feedback de busca -->
        <div
          v-if="checandoContato"
          class="mt-1 text-muted small d-flex align-items-center"
        >
          <b-spinner
            small
            type="grow"
            class="mr-2"
          />
          Verificando contato...
        </div>

        <!-- Contato encontrado -->
        <div
          v-else-if="contatoEncontrado"
          class="text-success mt-1 small"
        >
          Contato encontrado: <strong>{{ contatoEncontrado.nome }}</strong>
        </div>

        <!-- AVISO quando já existe conversa com este número -->
        <b-alert
          v-if="conversaExistente"
          show
          variant="warning"
          class="mt-2 p-2"
        >
          Este número já possui uma conversa em andamento com
          <strong>{{ conversaExistente.nome }}</strong>. Você pode abrir a conversa existente.
        </b-alert>

        <!-- AVISO quando não há contato salvo (mantém a regra nova) -->
        <div
          v-else-if="!checandoContato && !contatoEncontrado && numeroValido"
          class="mt-2 small text-muted"
        >
          Contato não encontrado nos contatos salvos. Um novo contato será criado ao enviar um template.
        </div>

        <!-- Campo NOME quando não há contato salvo -->
        <b-form-group
          v-if="!checandoContato && numeroValido && !contatoEncontrado && !abrindoPorContato"
          label="Nome do contato"
          label-size="sm"
          class="mt-2"
        >
          <b-form-input
            ref="nomeInput"
            v-model.trim="nomeNovoContato"
            size="sm"
            :state="nomeNovoContato.length >= 2 ? true : null"
            placeholder="Digite o nome do contato"
          />
          <small class="text-muted d-block mt-1">
            Esse nome será salvo junto ao novo contato.
          </small>
        </b-form-group>

        <!-- Seleção de Template -->
        <b-form-group
          v-if="(contatoEncontrado || numeroValido) && templatesDisponiveis.length"
          label="Template do WhatsApp"
          label-size="sm"
          class="mt-3"
        >
          <div class="template-selector">
            <div
              v-if="!templateSelecionado"
              class="template-empty"
              @click="abrirModalTemplates"
            >
              <feather-icon
                icon="FileTextIcon"
                size="32"
                class="text-muted mb-2"
              />
              <p class="mb-1">
                <strong>Selecionar Template</strong>
              </p>
              <small class="text-muted">Clique para escolher um template aprovado</small>
            </div>
            <div
              v-else
              class="template-selected"
            >
              <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center">
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="20"
                    class="text-success mr-2"
                  />
                  <div>
                    <strong>{{ templateSelecionado.name }}</strong>
                    <br>
                    <small class="text-muted">{{ templateSelecionado.preview }}</small>
                  </div>
                </div>
                <div>
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    @click="abrirModalTemplates"
                  >
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="14"
                      class="mr-50"
                    />
                    Trocar
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    size="sm"
                    class="ml-1"
                    @click="removerTemplate"
                  >
                    <feather-icon
                      icon="TrashIcon"
                      size="14"
                    />
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </b-form-group>

        <!-- Ações -->
        <div class="d-flex justify-content-end mt-2">
          <b-button
            size="sm"
            variant="secondary"
            class="mr-2"
            @click="fechar"
          >
            Cancelar
          </b-button>

          <!-- Se já existir conversa, ofereça abrir -->
          <b-button
            v-if="conversaExistente"
            size="sm"
            variant="primary"
            @click="abrirConversaExistente"
          >
            Abrir conversa
          </b-button>

          <!-- Se não existir, mantém Enviar -->
          <b-button
            v-else
            size="sm"
            variant="success"
            :disabled="!podeEnviar"
            @click="enviar"
          >
            Enviar
          </b-button>
        </div>
      </div>
    </b-modal>

    <!-- Modal de Seleção de Templates -->
    <modal-selecao-templates
      v-model="modalTemplatesVisivel"
      :templates="templatesDisponiveis"
      :carregando="checandoContato"
      :show-category="true"
      @template-selecionado="onTemplateSelecionado"
    >
      <template #info-text>
        <strong>Apenas templates aprovados</strong> pelo WhatsApp estão disponíveis para iniciar conversas.
      </template>
      <template #empty-text>
        Crie templates e aguarde a aprovação do WhatsApp para utilizá-los.
      </template>
    </modal-selecao-templates>
  </div>
</template>

<script>
import { BModal, BFormGroup, BFormSelect, BFormInput, BAlert, BButton, BSpinner, BBadge, BCard, BCardBody } from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { buscaContato, listarTemplates } from '../../functions/basics/chat'
import ModalSelecaoTemplates from '@core/components/modal-selecao-templates/ModalSelecaoTemplates.vue'

export default {
  name: 'NovaConversaModal',
  components: {
    BModal,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BAlert,
    BButton,
    BSpinner,
    BBadge,
    BCard,
    BCardBody,
    FeatherIcon,
    ModalSelecaoTemplates,
  },
  props: {
    conversas: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      visivel: false,
      contatoEncontrado: null,
      checandoContato: false,
      codigoPais: '55',
      numero: '',
      mascaraAtual: '###############',
      templateSelecionado: null,
      nomeNovoContato: '', // << NOVO
      abrindoPorContato: false,
      modalTemplatesVisivel: false, // << NOVO
      paises: [
        { value: '55', text: '🇧🇷 +55' },
        { value: '1', text: '🇺🇸 +1' },
        { value: '91', text: '🇮🇳 +91' },
        { value: '44', text: '🇬🇧 +44' },
        { value: '33', text: '🇫🇷 +33' },
      ],
      mascarasPorPais: {
        55: '(##) #####-####',
        1: '(###) ###-####',
        91: '#####-#####',
        44: '#####-#####',
        33: '## ## ## ##',
      },
      limitesPorPais: {
        55: 11,
        1: 10,
        91: 10,
        44: 10,
        33: 9,
      },
      templatesDisponiveis: [],
    }
  },
  computed: {
    podeEnviar() {
      // número precisa ser válido
      if (!this.numeroValido) return false
      // se já existe contato, pode enviar direto
      if (this.contatoEncontrado) return true
      // se não existir contato, exigir nome preenchido (≥ 2 chars)
      return this.nomeNovoContato.trim().length >= 2
    },
    numeroLimpo() {
      return this.numero.replace(/\D/g, '')
    },
    numeroValido() {
      const limite = this.limitesPorPais[this.codigoPais]
      return this.numeroLimpo.length === limite
    },
    numeroCompleto() {
      return this.codigoPais + this.numeroLimpo
    },
    conversaExistente() {
      if (!this.numeroValido) return null

      const candidatos = this.normalizarNumero(this.numeroCompleto)

      // Procura em this.conversas por qualquer equivalência
      for (const c of this.conversas) {
        const whats = this.soNumeros(c?.cliente?.whatsapp || '')
        const formatosConversa = this.normalizarNumero(whats)

        const bateu = candidatos.some(n => formatosConversa.includes(n))
        if (bateu) return c
      }
      return null
    },
    nomeContatoSugerido() {
      // Prioridade: contatoEncontrado > conversaExistente > vazio
      if (this.contatoEncontrado?.nome) return this.contatoEncontrado.nome
      if (this.conversaExistente?.nome) return this.conversaExistente.nome
      return ''
    },
  },
  watch: {
    nomeContatoSugerido: {
      immediate: true,
      handler(novoNome) {
        // Preenche automaticamente o nome quando encontra contato ou conversa existente
        if (novoNome && !this.nomeNovoContato) {
          this.nomeNovoContato = novoNome
        }
      },
    },
  },
  mounted() {
    this.aplicarMascara()
  },
  methods: {
    soNumeros(v) {
      return (v || '').toString().replace(/\D/g, '')
    },
    normalizarPhoneContato(phone) {
      // Remove tudo que não é dígito e retira prefixo 55 se existir
      let n = this.soNumeros(phone)
      if (n.startsWith('55')) n = n.slice(2)
      // Agora n deve estar no formato nacional (DDD + número)
      if (n.length === 11) {
        // DDD(2) + 9 + 8 dígitos
        return n
      }
      if (n.length === 10) {
        // DDD(2) + 8 dígitos -> inserir 9º após DDD
        return `${n.slice(0, 2)}9${n.slice(2)}`
      }
      if (n.length === 9 && n.charAt(0) === '9') {
        // 9 + 8 dígitos sem DDD -> assumir DDD 11
        return `11${n}`
      }
      if (n.length === 8) {
        // 8 dígitos sem DDD -> assumir DDD 11 e 9º dígito
        return `119${n}`
      }
      return n
    },
    normalizarNumero(numeroComCC) {
      // Retorna todos os formatos possíveis que queremos comparar
      const n = this.soNumeros(numeroComCC)

      // Se for BR (55) e tiver tamanho de celular com 9º dígito, gera também versão sem o 9
      if (n.startsWith('55') && n.length >= 12) {
        // CC (55) + DDD (2) + 9º (1) + resto
        const semNono = n.slice(0, 4) + n.slice(5) // remove o dígito logo após CC+DDD
        return [n, semNono]
      }
      return [n]
    },
    abrir() {
      this.visivel = true
      this.$nextTick(() => this.$refs.input?.focus())
    },

    abrirComContato(dadosContato) {
      // Preencher dados do contato
      if (dadosContato.phone) {
        this.abrindoPorContato = true
        this.codigoPais = '55' // Brasil por padrão
        this.numero = this.normalizarPhoneContato(dadosContato.phone)
      }

      // Não preencher o nome automaticamente
      this.nomeNovoContato = ''

      // Aplicar máscara
      this.aplicarMascara()

      // Abrir modal
      this.visivel = true

      this.$nextTick(() => {
        // Garantir que os dados sejam definidos após o modal estar visível
        if (dadosContato.phone) {
          this.numero = this.normalizarPhoneContato(dadosContato.phone)
        }
        // Não preencher o nome automaticamente
        this.nomeNovoContato = ''
        // Verificar automaticamente o número preenchido
        this.verificarContato()

          this.$refs.input?.focus()
      })
    },
    fechar() {
      this.visivel = false
    },
    abrirConversaExistente(conversa) {
      // Emitir evento para o componente pai
      this.$emit('abrir-conversa-existente', conversa)
      this.fechar()
    },
    resetar() {
      // limpa estado quando o modal fecha
      this.numero = ''
      this.contatoEncontrado = null
      this.templateSelecionado = null
      this.templatesDisponiveis = []
      this.checandoContato = false
      this.nomeNovoContato = '' // limpa também o nome novo
      this.abrindoPorContato = false
    },
    categoriaBadgeVariant(categoria) {
      const map = { MARKETING: 'primary', UTILITY: 'info', TRANSACTIONAL: 'success' }
      return map[categoria] || 'secondary'
    },
    abrirModalTemplates() {
      this.modalTemplatesVisivel = true
    },
    onTemplateSelecionado(template) {
      this.templateSelecionado = template
      this.modalTemplatesVisivel = false
    },
    removerTemplate() {
      this.templateSelecionado = null
    },
    verificarContatoSeNecessario() {
      // Só verifica se o usuário realmente digitou algo (não quando é pré-preenchido)
      if (this.numero && this.numero.length > 0) {
        this.verificarContato()
      }
    },
    async verificarContato() {
      const numeroCompleto = this.codigoPais + this.numeroLimpo
      if (this.numeroValido) {
        this.checandoContato = true
        this.contatoEncontrado = null
        try {
          const contato = await buscaContato(this.$route.params.uuid, numeroCompleto)
          if (contato && contato.nome) {
            this.contatoEncontrado = contato
            this.nomeNovoContato = '' // limpa, pois já há contato
          } else {
            // não achou contato → foca o input de nome
            this.contatoEncontrado = null
            this.$nextTick(() => this.$refs.nomeInput?.focus())
          }

          const resposta = await listarTemplates(this.$route.params.uuid, true) // Apenas aprovados
          if (resposta && resposta.templates) {
            this.templatesDisponiveis = resposta.templates
              .map(t => ({
                id: t.id,
                name: t.name,
                category: t.category,
                preview: (t.components.find(c => c.type === 'BODY') || {}).text || '',
              }))
          }
        } catch (e) {
          this.contatoEncontrado = null
          this.templatesDisponiveis = []
        } finally {
          this.checandoContato = false
        }
      } else {
        this.contatoEncontrado = null
        this.templatesDisponiveis = []
      }
    },
    aplicarMascara() {
      this.mascaraAtual = this.mascarasPorPais[this.codigoPais] || '###############'
    },
    filtrarNumeros() {
      let somenteNumeros = this.numero.replace(/\D/g, '')
      const limite = this.limitesPorPais[this.codigoPais]
      if (somenteNumeros.length > limite) {
        somenteNumeros = somenteNumeros.slice(0, limite)
      }
      this.numero = somenteNumeros
    },
    abrirConversaExistente() {
      if (this.conversaExistente) {
        this.$emit('abrir-conversa-existente', this.conversaExistente)
        this.fechar()
      }
    },
    enviar() {
      const numeroCompleto = this.codigoPais + this.numeroLimpo

      // Se por algum motivo a conversa apareceu agora, prioriza abrir
      if (this.conversaExistente) {
        this.abrirConversaExistente()
        return
      }

      // se achou contato, usa o nome dele; senão, usa o nome digitado
      const nome = this.contatoEncontrado
        ? this.contatoEncontrado.nome
        : this.nomeNovoContato.trim()

      this.$emit('nova-conversa', {
        numero: numeroCompleto,
        nome,
        template: this.templateSelecionado,
      })
      this.fechar()
    },
  },
}
</script>

<style scoped>
/* Template Selector */
.template-selector {
  margin-bottom: 0.5rem;
}

.template-empty {
  border: 2px dashed #d8d6de;
  border-radius: 12px;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8f9fa;
}

.template-empty:hover {
  border-color: #7367f0;
  background: #f8f7ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15);
}

.template-selected {
  border: 2px solid #28c76f;
  border-radius: 12px;
  padding: 1rem;
  background: #f0fff4;
  transition: all 0.3s ease;
}

.template-selected:hover {
  box-shadow: 0 4px 12px rgba(40, 199, 111, 0.2);
}
</style>
